import type { RequestConfig } from '@patsnap/common-ps-http'
import { merge, noop } from 'lodash'
import type { ErrorHandlerService, FErrorDisplay } from './error-handler-service'

// 允许不登录
export interface CanBeAnonymousRequestConfig extends RequestConfig {
    anonymous?: boolean;
}

export abstract class BaseHttpService {
    constructor(errorHandlerService: ErrorHandlerService) {
        this._errorDisplayProxy = errorHandlerService.errorDisplay
    }

    // #region 通用处理相关
    _errorDisplayProxy: FErrorDisplay = noop

    /**
     * 用于后期显示设置, 有的错误必须依靠 vue root
     * @param fn
     */
    setErrorDisplay(fn: FErrorDisplay): void {
        this._errorDisplayProxy = fn
    }
    // #endregion

    abstract request<T = any>(_config: CanBeAnonymousRequestConfig): Promise<T>

    get<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'get',
        }, config)
        return this.request<T>(config)
    }

    post<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'post',
        }, config)
        return this.request<T>(config)
    }

    postForm<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'post',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
        }, config)
        return this.request<T>(config)
    }

    postMultipartForm<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'post',
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }, config)
        return this.request<T>(config)
    }

    put<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'put',
        }, config)
        return this.request<T>(config)
    }

    patch<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'patch',
        }, config)
        return this.request<T>(config)
    }

    patchForm<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'patch',
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }, config)
        return this.request<T>(config)
    }

    delete<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        config = merge({
            method: 'delete',
        }, config)
        return this.request<T>(config)
    }

    getPlainJson<T = any>(config: CanBeAnonymousRequestConfig): Promise<T> {
        /* 直接XHR请求s3可能会有跨域问题，Model Federation host 本地开发中必定产生跨域问题 */
        return fetch(config.url as string, { mode: 'cors', credentials: 'omit' })
            .then(res => res.json())
    }

    withLoading<T extends { loading: boolean }, F>(self: T, p: Promise<F>, useLoading = true): Promise<F> {
        if (!useLoading) {
            return p
        }
        self.loading = true
        return p.finally(() => {
            self.loading = false
        })
    }

    withFieldLoading<T extends object, F extends keyof T, K>(self: T, p: Promise<K>, field: F): Promise<K> {
        (self[field] as any) = true
        return p.finally(() => {
            (self[field] as any) = false
        })
    }
}
